<script>
    import { tick, getContext, onDestroy } from 'svelte';
    import { writable } from 'svelte/store';

    /**
     *  a writable store that emits Map<string, truthy>
     *  components (especially notebooks) update the map with their unique id - usually a random uuid -
     *  and their state, either false (not ready, still loading) or true (ready for printing)
     *
     *  The window.print() function will only be called once all values in the map are truthy
     **/
    const readyStore = getContext('readyStore');

    if (!readyStore) {
      throw new Error('invalid context, key readyStore must return a writable store')
    }

    /**
     * @property a writable store with a boolean value
     *
     * the print button will toggle this value when clicked,
     *
     * when this emits true, the report is going to be printed, and components can listen for this
     * to adjust their content to suit printed reports - i.e. replacing a tabbed layout when only
     * the active tab is rendered, with a simple layout with every tab after one another
     **/
    const printStore = getContext('printStore');

    if (!printStore) {
        throw new Error('invalid context, key printStore must return a writable store')
    }

    /**
     * a store that updates when a print is requested
     *
     * write to this to manually trigger a print
     *
     * for example
     *
     * requestPrintStore.set(true)
     *
     * this will trigger
     *
     * printStore to be true
     *
     * then components listening to printStore, may set their readyStore values to false, and print will
     * delay until the ready store contains all truthy values
     */
    const requestPrintStore = getContext('requestPrintStore') || writable(false);

    export let beforePrint = null
    export let afterPrint = null

    let pending = false

    let pctDone = 0;

    let unsub;

    async function togglePrint () {
        if (pending) {
          if (unsub) {
            unsub();
            unsub = undefined;
          }
          pending=  false;
          return;
        }
        if (printStore) printStore.set(true)
        pending = true

        await tick()

        let callback = async state => {
            const completed = [...state.values()].filter(s => !!s);
            pctDone = Math.round(100 * completed.length / (state.size || 1));

            if (completed.length === state.size) {
                pending = false

                if (unsub) {
                    unsub();
                    unsub = undefined;
                }

                await tick();

                if (beforePrint) beforePrint()
                window.print();
                if (afterPrint) afterPrint()
                if (printStore) printStore.set(false)

                if (unsub) {
                    unsub();
                    unsub = undefined;
                }
            }
        };

        unsub = readyStore.subscribe(callback);

        // TODO - implement some sort of timeout behaviour
    }

    $: if ($requestPrintStore) {
        togglePrint();
    }

    onDestroy(function () {
        if (unsub) {
            unsub();
            unsub = undefined;
        }
    });
</script>

<style>
    button {
        background: none;
        font-weight: bold;
        padding: 0;
        text-transform: uppercase;
        border: none;
        margin: 0 0;
        color: #f4f4f4;
    }

    div.inline {
        display: inline-flex;
        align-items: center;
        color: #f4f4f4;
    }

    span {
        font-size: 1.2em;
        padding-left: 0.25em;
    }
</style>

<div class="inline">
    <button on:click={togglePrint}
            title="Print"
            disabled={pending}>
        <i class="icon icon-print"
           class:active={pending}
           aria-label="Print"
           data-action="print"></i>
    </button>

    {#if pending}
        <span>
            {pctDone}%
        </span>
    {/if}
</div>


